import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router-dom';
import { fetchMachinesNightscan } from '../actions/machines';

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Convert a SleepStart/SleepEnd value (e.g. 2230 → 22:30) to minutes since midnight */
function sleepToMinutes(val) {
  if (val == null || isNaN(val)) return null;
  const h = Math.floor(val / 100);
  const m = val % 100;
  return h * 60 + m;
}

/** Format minutes since midnight as "HH:MM" */
function minutesToHHMM(mins) {
  const h = Math.floor(((mins % 1440) + 1440) % 1440 / 60);
  const m = ((mins % 1440) + 1440) % 1440 % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Format a duration in minutes as "Xh Ym" */
function formatDuration(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

/**
 * Given an array of machines, compute:
 *  - blockedMinutes: sorted merged array of [start, end] intervals (blocked = inside sleep)
 *  - freeWindows: sorted array of { start, end, duration }
 *
 * "No maintenance between SleepStart and SleepEnd" → blocked = sleep period.
 */
function computeWindows(machines) {
  const raw = [];

  for (const m of machines) {
    const start = sleepToMinutes(m.SleepStart);
    const end = sleepToMinutes(m.SleepEnd);
    if (start === null || end === null || start === end) continue;

    if (start > end) {
      // Wraps midnight → two intervals
      raw.push([start, 1440]);
      raw.push([0, end]);
    } else {
      raw.push([start, end]);
    }
  }

  // Sort and merge overlapping blocked intervals
  raw.sort((a, b) => a[0] - b[0]);
  const blocked = [];
  for (const iv of raw) {
    if (blocked.length === 0 || blocked[blocked.length - 1][1] < iv[0]) {
      blocked.push([...iv]);
    } else {
      blocked[blocked.length - 1][1] = Math.max(blocked[blocked.length - 1][1], iv[1]);
    }
  }

  // Free windows = complement of blocked in [0, 1440]
  const free = [];
  let cursor = 0;
  for (const [bs, be] of blocked) {
    if (cursor < bs) {
      free.push({ start: cursor, end: bs, duration: bs - cursor });
    }
    cursor = be;
  }
  if (cursor < 1440) {
    free.push({ start: cursor, end: 1440, duration: 1440 - cursor });
  }

  // Sort free windows by duration descending (optimal first)
  free.sort((a, b) => b.duration - a.duration);

  return { blocked, free };
}

// ─── 24-hour Timeline Bar ────────────────────────────────────────────────────

const TimelineBar = ({ blocked, free }) => {
  const TOTAL = 1440;
  const toPct = (mins) => (mins / TOTAL) * 100;

  // Hour tick marks
  const ticks = [0, 2, 4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24];

  return (
    <div style={{ marginBottom: 12 }}>
      {/* Bar */}
      <div style={{
        position: 'relative',
        height: 28,
        background: '#e8f5e9',
        borderRadius: 6,
        overflow: 'hidden',
        border: '1px solid #c8e6c9',
      }}>
        {/* Blocked (sleep) segments */}
        {blocked.map(([s, e], i) => (
          <div key={i} style={{
            position: 'absolute',
            left: `${toPct(s)}%`,
            width: `${toPct(e - s)}%`,
            height: '100%',
            background: 'rgba(239,83,80,0.35)',
            borderLeft: '1px solid rgba(239,83,80,0.6)',
            borderRight: '1px solid rgba(239,83,80,0.6)',
          }} title={`Bloqueado: ${minutesToHHMM(s)} – ${minutesToHHMM(e)}`} />
        ))}
        {/* Best free window highlight */}
        {free.length > 0 && (
          <div style={{
            position: 'absolute',
            left: `${toPct(free[0].start)}%`,
            width: `${toPct(free[0].duration)}%`,
            height: '100%',
            background: 'rgba(0,150,136,0.25)',
            borderLeft: '2px solid #00897b',
            borderRight: '2px solid #00897b',
          }} title={`Ventana óptima: ${minutesToHHMM(free[0].start)} – ${minutesToHHMM(free[0].end)}`} />
        )}
      </div>
      {/* Hour ticks */}
      <div style={{ position: 'relative', height: 16, fontSize: 10, color: '#888', marginTop: 2 }}>
        {ticks.map(h => (
          <span key={h} style={{
            position: 'absolute',
            left: `${(h / 24) * 100}%`,
            transform: 'translateX(-50%)',
          }}>{String(h % 24).padStart(2, '0')}:00</span>
        ))}
      </div>
    </div>
  );
};

// ─── Customer Group Card ─────────────────────────────────────────────────────

const CustomerCard = ({ customerName, machines }) => {
  const [expanded, setExpanded] = useState(false);
  const { blocked, free } = useMemo(() => computeWindows(machines), [machines]);

  const bestWindow = free[0] || null;
  const hasMachinesWithSleep = machines.some(
    m => m.SleepStart != null && m.SleepEnd != null && m.SleepStart !== m.SleepEnd
  );

  return (
    <div style={{
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      marginBottom: 16,
      overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Card header */}
      <div
        onClick={() => setExpanded(e => !e)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          cursor: 'pointer',
          background: '#fafafa',
          borderBottom: expanded ? '1px solid #e0e0e0' : 'none',
          userSelect: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontWeight: 700, fontSize: 15, color: '#212121' }}>{customerName}</span>
          <span style={{
            fontSize: 12,
            background: '#eeeeee',
            color: '#616161',
            borderRadius: 20,
            padding: '2px 8px',
          }}>{machines.length} {machines.length === 1 ? 'máquina' : 'máquinas'}</span>
          {bestWindow && (
            <span style={{
              fontSize: 12,
              background: '#e8f5e9',
              color: '#2e7d32',
              borderRadius: 20,
              padding: '2px 8px',
              fontWeight: 600,
            }}>
              Mejor window: {minutesToHHMM(bestWindow.start)}–{minutesToHHMM(bestWindow.end)} ({formatDuration(bestWindow.duration)})
            </span>
          )}
          {!hasMachinesWithSleep && (
            <span style={{
              fontSize: 12,
              background: '#fff3e0',
              color: '#e65100',
              borderRadius: 20,
              padding: '2px 8px',
            }}>Sin datos de sleep</span>
          )}
        </div>
        <span style={{ fontSize: 18, color: '#bdbdbd' }}>{expanded ? '▲' : '▼'}</span>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '16px' }}>
          {hasMachinesWithSleep ? (
            <>
              <TimelineBar blocked={blocked} free={free} />

              {/* Legend */}
              <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 12, color: '#616161', flexWrap: 'wrap' }}>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(239,83,80,0.35)', border: '1px solid rgba(239,83,80,0.6)', borderRadius: 2, marginRight: 4 }} />Bloqueado (Sleep)</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: 'rgba(0,150,136,0.25)', border: '2px solid #00897b', borderRadius: 2, marginRight: 4 }} />Ventana óptima</span>
                <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#e8f5e9', border: '1px solid #c8e6c9', borderRadius: 2, marginRight: 4 }} />Libre</span>
              </div>

              {/* Free windows table */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#424242', marginBottom: 8 }}>
                  Ventanas de mantenimiento disponibles
                </div>
                {free.length === 0 ? (
                  <div style={{ color: '#e53935', fontSize: 13 }}>
                    No hay ventanas disponibles — todos los horarios están bloqueados.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f5f5f5' }}>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#616161', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>#</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#616161', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Inicio</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#616161', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Fin</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#616161', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Duración</th>
                        <th style={{ padding: '6px 10px', textAlign: 'left', color: '#616161', fontWeight: 600, borderBottom: '1px solid #e0e0e0' }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {free.map((w, idx) => (
                        <tr key={idx} style={{ background: idx === 0 ? '#f1f8f6' : 'white' }}>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', color: '#9e9e9e' }}>{idx + 1}</td>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{minutesToHHMM(w.start)}</td>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0', fontWeight: 600 }}>{minutesToHHMM(w.end)}</td>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>{formatDuration(w.duration)}</td>
                          <td style={{ padding: '6px 10px', borderBottom: '1px solid #f0f0f0' }}>
                            {idx === 0 ? (
                              <span style={{ background: '#e8f5e9', color: '#2e7d32', borderRadius: 20, padding: '2px 8px', fontWeight: 600, fontSize: 11 }}>ÓPTIMA</span>
                            ) : (
                              <span style={{ background: '#f5f5f5', color: '#757575', borderRadius: 20, padding: '2px 8px', fontSize: 11 }}>Alternativa</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Machine list */}
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, color: '#424242', marginBottom: 8 }}>
                  Máquinas incluidas
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {machines.map(m => (
                    <Link
                      key={m._id}
                      to={`/machines/${m._id}`}
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        background: '#f9f9f9',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8,
                        padding: '6px 10px',
                        textDecoration: 'none',
                        minWidth: 120,
                      }}
                    >
                      <span style={{ fontWeight: 700, fontSize: 12, color: '#212121' }}>{m.Serial}</span>
                      <span style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                        Sleep: {m.SleepStart != null ? minutesToHHMM(sleepToMinutes(m.SleepStart)) : '–'}
                        {' → '}
                        {m.SleepEnd != null ? minutesToHHMM(sleepToMinutes(m.SleepEnd)) : '–'}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div style={{ color: '#9e9e9e', fontSize: 13 }}>
              Ninguna máquina de este cliente tiene configurado un período de sleep.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main View ───────────────────────────────────────────────────────────────

const MaintenanceWindowsView = ({ machinesNightscan, loading, fetchMachinesNightscan }) => {
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchMachinesNightscan();
  }, [fetchMachinesNightscan]);

  // Group machines by customer
  const grouped = useMemo(() => {
    if (!machinesNightscan) return [];
    const map = new Map();
    const EXCLUDED = ['SHOWROOM-I', 'SHOWROOM-O', 'VLK00000', 'VLK00001'];

    for (const m of machinesNightscan) {
      if (EXCLUDED.includes(m.Serial)) continue;
      const customerName = m.Customer?.Fullname || 'Sin cliente';
      if (!map.has(customerName)) map.set(customerName, []);
      map.get(customerName).push(m);
    }

    return Array.from(map.entries())
      .map(([name, machines]) => ({ name, machines }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [machinesNightscan]);

  // Global overview across all machines
  const globalWindows = useMemo(() => {
    if (!machinesNightscan) return null;
    return computeWindows(machinesNightscan);
  }, [machinesNightscan]);

  const filtered = useMemo(() => {
    if (!search.trim()) return grouped;
    const q = search.toLowerCase();
    return grouped.filter(g =>
      g.name.toLowerCase().includes(q) ||
      g.machines.some(m => m.Serial?.toLowerCase().includes(q))
    );
  }, [grouped, search]);

  const totalMachines = machinesNightscan?.length || 0;
  const machinesWithSleep = (machinesNightscan || []).filter(
    m => m.SleepStart != null && m.SleepEnd != null && m.SleepStart !== m.SleepEnd
  ).length;

  return (
    <div style={{ maxWidth: 960, margin: '0 auto', padding: '4px 0 40px' }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontWeight: 800, fontSize: 22, color: '#212121' }}>
          Ventanas de mantenimiento
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#757575' }}>
          Períodos óptimos para mantenimiento — calculados para que no coincidan con el sleep de ninguna máquina.
        </p>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 48, color: '#9e9e9e' }}>
          Cargando datos de máquinas…
        </div>
      )}

      {!loading && machinesNightscan && (
        <>
          {/* Summary stats */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
            {[
              { label: 'Máquinas totales', value: totalMachines, color: '#1976d2' },
              { label: 'Con sleep configurado', value: machinesWithSleep, color: '#388e3c' },
              { label: 'Clientes', value: grouped.length, color: '#7b1fa2' },
              globalWindows?.free[0]
                ? { label: 'Mejor ventana global', value: formatDuration(globalWindows.free[0].duration), sub: `${minutesToHHMM(globalWindows.free[0].start)} – ${minutesToHHMM(globalWindows.free[0].end)}`, color: '#00897b' }
                : { label: 'Mejor ventana global', value: '—', color: '#9e9e9e' },
            ].map((stat, i) => (
              <div key={i} style={{
                flex: '1 1 160px',
                background: '#fff',
                border: '1px solid #e0e0e0',
                borderLeft: `4px solid ${stat.color}`,
                borderRadius: 8,
                padding: '12px 16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
              }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: stat.color }}>{stat.value}</div>
                {stat.sub && <div style={{ fontSize: 11, color: '#616161' }}>{stat.sub}</div>}
                <div style={{ fontSize: 12, color: '#9e9e9e', marginTop: 2 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Global 24h timeline */}
          {globalWindows && (
            <div style={{
              background: '#fff',
              border: '1px solid #e0e0e0',
              borderRadius: 10,
              padding: '16px',
              marginBottom: 24,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#424242', marginBottom: 12 }}>
                Vista global — todas las máquinas
              </div>
              <TimelineBar blocked={globalWindows.blocked} free={globalWindows.free} />
              <div style={{ fontSize: 12, color: '#757575', marginTop: 4 }}>
                Bloqueados por al menos una máquina:&nbsp;
                {globalWindows.blocked.map(([s, e], i) => (
                  <span key={i} style={{ marginRight: 8, color: '#e53935', fontWeight: 600 }}>
                    {minutesToHHMM(s)}–{minutesToHHMM(e)}
                  </span>
                ))}
                {globalWindows.blocked.length === 0 && <span style={{ color: '#2e7d32' }}>Ningún período bloqueado.</span>}
              </div>
            </div>
          )}

          {/* Search */}
          <div style={{ marginBottom: 16 }}>
            <input
              type="text"
              placeholder="Buscar por cliente o serial…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 14px',
                border: '1px solid #e0e0e0',
                borderRadius: 8,
                fontSize: 13,
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
          </div>

          {/* Customer cards */}
          {filtered.length === 0 && (
            <div style={{ textAlign: 'center', color: '#9e9e9e', padding: 32 }}>
              No hay resultados.
            </div>
          )}
          {filtered.map(({ name, machines }) => (
            <CustomerCard key={name} customerName={name} machines={machines} />
          ))}
        </>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  machinesNightscan: state.machines.machinesNightscan,
  loading: state.machines.loading,
});

const mapDispatchToProps = (dispatch) => ({
  fetchMachinesNightscan: bindActionCreators(fetchMachinesNightscan, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceWindowsView);
